if ( typeof ProductQuantity !== 'function' ) {

	class ProductQuantity extends HTMLElement {

		constructor(){

			super();

			const qty = this.querySelector('.qty-selector'),
						qtyMinus = this.querySelector('.qty-minus'),
						qtyPlus = this.querySelector('.qty-plus'),
						qtyMin = qty.getAttribute('min') ? parseInt(qty.getAttribute('min')) : 1,
						qtyMax = () => qty.hasAttribute('max') ? Number(qty.max) : 999;

			if ( this.hasAttribute('data-live-cart-quantity') ) {
				this.initLiveCart(qty, qtyMinus, qtyPlus);
				return;
			}

			const updateButtons = () => {
				const currentQty = parseInt(qty.value);
				qtyMinus.disabled = currentQty <= qtyMin;
				qtyPlus.disabled = currentQty >= qtyMax();
				[qtyMinus, qtyPlus].forEach(button => {
					if ( button.disabled ) button.classList.add('disabled');
					else button.classList.remove('disabled');
				});
			};

			updateButtons();

			qty.addEventListener('change', () => {
				qty.value = String(Math.max(qtyMin, Math.min(qtyMax(), parseInt(qty.value) || qtyMin)));
				updateButtons();
			});

			qtyMinus.addEventListener('click', event => {
				event.preventDefault();
				if ( qtyMinus.disabled ) return;
				qty.value = String(Math.max(qtyMin, parseInt(qty.value) - 1));
				updateButtons();
			});

			qtyPlus.addEventListener('click', event => {
				event.preventDefault();
				if ( qtyPlus.disabled ) return;
				qty.value = String(Math.min(qtyMax(), parseInt(qty.value) + 1));
				updateButtons();
			});

		}

		initLiveCart(qty, qtyMinus, qtyPlus) {
			this.qty = qty;
			this.qtyMinus = qtyMinus;
			this.qtyPlus = qtyPlus;
			this.serverQuantity = 0;
			this.desiredQuantity = 0;
			this.updating = false;
			this.ready = false;
			this.otherProductQuantity = 0;
			this.dataset.loading = 'true';

			qtyMinus.addEventListener('click', event => {
				event.preventDefault();
				if ( ! this.ready || this.desiredQuantity <= 0 ) return;
				this.setDesiredQuantity(this.desiredQuantity - 1);
			});

			qtyPlus.addEventListener('click', event => {
				event.preventDefault();
				if ( ! this.ready || this.desiredQuantity >= this.maximumQuantity() ) return;
				window.PaleoCartAnimation?.play(qtyPlus);
				this.setDesiredQuantity(this.desiredQuantity + 1);
			});

			document.addEventListener('cart-updated', event => {
				if ( event.detail?.source === this ) return;
				if ( event.detail?.cart ) this.syncFromCart(event.detail.cart);
				else this.loadCart();
			});

			const variants = this.closest('[data-js-product-component]')?.querySelector('product-variants');
			variants?.addEventListener('VARIANT_CHANGE', () => this.loadCart());

			this.loadCart();
		}

		getVariantId() {
			return String(this.closest('product-form')?.querySelector('input[name="id"]')?.value || '');
		}

		quantityFromCart(cart, variantId = this.getVariantId()) {
			return (cart.items || []).reduce((total, item) => {
				return String(item.variant_id) === String(variantId) ? total + Number(item.quantity || 0) : total;
			}, 0);
		}

		renderQuantity(quantity) {
			this.qty.value = String(quantity);
			this.qtyMinus.disabled = ! this.ready || quantity <= 0;
			this.qtyPlus.disabled = ! this.ready || quantity >= this.maximumQuantity();
		}

		maximumQuantity() {
			const limit = Number(this.dataset.orderLimit);
			return Number.isSafeInteger(limit) && limit > 0 ? Math.max(0, limit - this.otherProductQuantity) : Infinity;
		}

		updateProductQuantity(cart) {
			this.otherProductQuantity = (cart.items || []).reduce((total, item) => {
				return String(item.product_id) === this.dataset.productId && String(item.variant_id) !== this.getVariantId() ? total + Number(item.quantity || 0) : total;
			}, 0);
		}

		syncFromCart(cart) {
			this.updateProductQuantity(cart);
			const quantity = this.quantityFromCart(cart);
			this.serverQuantity = quantity;
			this.desiredQuantity = quantity;
			this.ready = true;
			this.dataset.loading = 'false';
			this.renderQuantity(quantity);
		}

		async loadCart() {
			const variantId = this.getVariantId();
			if ( ! variantId ) return;
			this.ready = false;
			this.dataset.loading = 'true';
			this.renderQuantity(0);
			try {
				const response = await fetch(this.dataset.cartUrl, { cache: 'no-store' });
				if ( ! response.ok ) throw new Error(await response.text());
				if ( variantId !== this.getVariantId() ) return this.loadCart();
				this.syncFromCart(await response.json());
			} catch (error) {
				console.error('Product quantity load failed', error);
				this.dataset.loading = 'false';
			}
		}

		setDesiredQuantity(quantity) {
			this.desiredQuantity = Math.max(0, quantity > this.desiredQuantity ? Math.min(this.maximumQuantity(), quantity) : quantity);
			this.renderQuantity(this.desiredQuantity);
			this.flushQuantity();
		}

		async flushQuantity() {
			if ( this.updating || ! this.ready ) return;
			if ( this.desiredQuantity === this.serverQuantity ) return;

			const variantId = this.getVariantId();
			const requestedQuantity = this.desiredQuantity;
			this.updating = true;
			this.dataset.loading = 'true';

			try {
				const response = await fetch(this.dataset.cartUpdateUrl, {
					method: 'POST',
					headers: {
						'Content-Type': 'application/json',
						'Accept': 'application/json',
						'X-Requested-With': 'XMLHttpRequest'
					},
					body: JSON.stringify({ updates: { [variantId]: requestedQuantity } })
				});
				if ( ! response.ok ) throw new Error(await response.text());

				const cart = await response.json();
				this.updateProductQuantity(cart);
				if ( variantId !== this.getVariantId() ) {
					this.updating = false;
					return this.loadCart();
				}

				this.serverQuantity = this.quantityFromCart(cart, variantId);
				if ( this.desiredQuantity === requestedQuantity ) {
					this.desiredQuantity = this.serverQuantity;
					this.renderQuantity(this.serverQuantity);
				}

				this.updateHeader(cart);
				document.dispatchEvent(new CustomEvent('cart-updated', { detail: { cart, source: this } }));

				if ( this.desiredQuantity === this.serverQuantity ) await this.refreshDrawer();
			} catch (error) {
				console.error('Product quantity update failed', error);
				this.desiredQuantity = this.serverQuantity;
				this.renderQuantity(this.serverQuantity);
			} finally {
				this.updating = false;
				this.dataset.loading = 'false';
				if ( this.desiredQuantity !== this.serverQuantity ) this.flushQuantity();
			}
		}

		updateHeader(cart) {
			document.querySelectorAll('[data-header-cart-count]').forEach(element => {
				element.textContent = String(cart.item_count || 0);
			});
			const total = (Number(cart.total_price || 0) / 100).toLocaleString(document.documentElement.lang || undefined, {
				minimumFractionDigits: 2,
				maximumFractionDigits: 2
			});
			document.querySelectorAll('[data-header-cart-total]').forEach(element => {
				const suffix = (element.textContent || '').match(/\s[A-Z]{3}\s*$/)?.[0] || '';
				element.textContent = total + suffix;
			});
		}

		async refreshDrawer() {
			try {
				const separator = location.search ? '&' : '?';
				const response = await fetch(`${location.pathname}${location.search}${separator}section_id=helper-cart&_=${Date.now()}`, { cache: 'no-store' });
				if ( ! response.ok ) return;
				const freshDocument = new DOMParser().parseFromString(await response.text(), 'text/html');
				[['#AjaxCartForm', '#AjaxCartForm'], ['#AjaxCartSubtotal', '#AjaxCartSubtotal']].forEach(([freshSelector, oldSelector]) => {
					const fresh = freshDocument.querySelector(freshSelector);
					const old = document.querySelector(oldSelector);
					if ( fresh && old ) old.replaceWith(fresh);
				});
			} catch (error) {
				console.warn('Cart drawer refresh failed', error);
			}
		}
		
	}


  if ( typeof customElements.get('product-quantity') == 'undefined' ) {
    customElements.define('product-quantity', ProductQuantity);
	}

}
